import { useMemo, useState } from "react";
import { MailPlus, RefreshCw, Search, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/format";
import { errMsg } from "../../../lib/xutil";
import { useApi } from "../../customer/hooks";
import { Field, PageHead, Panel, btnGhost, btnPrimary, chip, input } from "../../../components/common/Page";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";

const TONE = { none: "bg-slate-100 text-slate-600", invited: "bg-amber-100 text-amber-800", active: "bg-brand/10 text-brand" };

const ERR = { "Unknown SoftOne customer code": "unknown", "Valid email and name are required": "invalid", "A user with this email already exists": "duplicate" };
const errText = (t, d) => (ERR[d] ? t(`x.onb.err.${ERR[d]}`) : d);

const InviteDialog = ({ row, onClose, onDone }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ email: row.email || "", name: "", role: row.users.some((u) => u.active) ? "buyer" : "approver" });
  const send = async () => {
    try { await api.post("/admin/users/invite", { ...f, customer_code: row.code }); toast.success(t("x.onb.sent")); onDone(); } catch (e) { toast.error(errText(t, e?.response?.data?.detail) || errMsg(t, e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="onb-invite-dialog">
        <DialogHeader><DialogTitle>{t("x.onb.inviteTo", { name: row.name })}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label={t("x.onb.email")}><input type="email" value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} className={input} data-testid="onb-invite-email" /></Field>
          <Field label={t("x.onb.person")}><input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} className={input} data-testid="onb-invite-name" /></Field>
          <div className="flex gap-2">{["approver", "buyer"].map((r) => <button key={r} type="button" className={chip(f.role === r)} onClick={() => setF((s) => ({ ...s, role: r }))} data-testid={`onb-invite-role-${r}`}>{t(`x.onb.roles.${r}`)}</button>)}</div>
          <button type="button" disabled={!f.email || f.name.trim().length < 2} onClick={send} className={btnPrimary} data-testid="onb-invite-send"><Send className="h-4 w-4" />{t("x.onb.send")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const parseRows = (text) => text.split("\n").map((l) => l.split(/[;,\t]/).map((x) => x.trim())).filter((p) => p[0] && p[1])
  .map(([customer_code, email, name, role]) => ({ customer_code, email, name: name || email.split("@")[0], ...(role === "buyer" || role === "approver" ? { role } : {}) }));

const BulkInvite = ({ onDone }) => {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const rows = parseRows(text);
  const send = async () => {
    try { const { data } = await api.post("/admin/users/bulk-invite", { rows }); setRes(data); toast.success(t("x.onb.bulkDone", { ok: data.invited, bad: data.failed })); onDone(); } catch (e) { toast.error(errMsg(t, e)); }
  };
  return (
    <Panel title={t("x.onb.bulk")} sub={t("x.onb.bulkSub")} testId="onb-bulk">
      <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={"T0001;purchasing@hotel.gr;Maria Papadopoulou;approver\nT0002;f&b@hotel.gr;Nikos"} className={`${input} h-auto py-2 font-mono text-xs`} data-testid="onb-bulk-text" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button type="button" disabled={!rows.length} onClick={send} className={btnPrimary} data-testid="onb-bulk-send"><MailPlus className="h-4 w-4" />{t("x.onb.bulkSend", { n: rows.length })}</button>
        <span className="text-xs text-slate-500">{t("x.onb.mocked")}</span>
      </div>
      {res && <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto text-xs" data-testid="onb-bulk-results">{res.results.map((r, i) => <li key={i} className={r.ok ? "text-brand" : "text-rose-600"}>{r.customer_code} · {r.email} · {r.ok ? "✓" : errText(t, r.error)}</li>)}</ul>}
    </Panel>
  );
};

const Row = ({ r, onInvite, onResend }) => {
  const { t } = useI18n();
  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-3" data-testid={`onb-row-${r.code}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-navy">{r.name} <span className="text-xs font-normal text-slate-500">{r.code} · {r.area} · {t("x.cust.rep")} {r.sales_rep}</span></p>
        {r.users.map((u) => (
          <p key={u.id} className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="font-medium text-navy">{u.name}</span>{u.email}
            <span className={`rounded-full px-2 py-0.5 ${!u.active ? "bg-slate-100 text-slate-400" : TONE[u.status === "active" ? "active" : "invited"]}`}>{!u.active ? t("x.cust.inactive") : t(`x.onb.userStatus.${u.status}`)}</span>
            {u.last_login_at && <span>{t("x.cust.lastLogin")}: {fmtDateTime(u.last_login_at)}</span>}
            {u.active && u.status === "invited" && <button type="button" onClick={() => onResend(u)} className="inline-flex h-8 items-center gap-1 rounded-full px-2 font-medium text-navy hover:bg-slate-100" data-testid={`onb-resend-${u.id}`}><RefreshCw className="h-3.5 w-3.5" />{t("x.onb.resend")}</button>}
          </p>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${TONE[r.state]}`} data-testid={`onb-state-${r.code}`}>{t(`x.onb.state.${r.state}`)}</span>
        <button type="button" onClick={() => onInvite(r)} className={`${btnGhost} h-10 px-4`} data-testid={`onb-invite-${r.code}`}><MailPlus className="h-4 w-4" />{t("x.onb.invite")}</button>
      </div>
    </li>
  );
};

export default function CustomerOnboarding() {
  const { t } = useI18n();
  const r = useApi("onboarding", "/admin/users/onboarding", undefined, { staleTime: 0 });
  const [q, setQ] = useState("");
  const [state, setState] = useState("all");
  const [invite, setInvite] = useState(null);
  const counts = useMemo(() => (r.data || []).reduce((a, x) => ({ ...a, [x.state]: (a[x.state] || 0) + 1 }), {}), [r.data]);
  const list = (r.data || []).filter((x) => (state === "all" || x.state === state)
    && (!q.trim() || `${x.code} ${x.name} ${x.legal_name || ""} ${x.area} ${x.users.map((u) => u.email).join(" ")}`.toLowerCase().includes(q.trim().toLowerCase())));
  const resend = async (u) => { try { await api.post(`/admin/users/${u.id}/resend-invite`); toast.success(t("x.onb.sent")); r.refetch(); } catch (e) { toast.error(errMsg(t, e)); } };
  return (
    <div className="space-y-5" data-testid="onboarding-page">
      <PageHead title={t("x.onb.title")} sub={t("x.onb.sub")} />
      <div className="grid grid-cols-3 gap-3">{["none", "invited", "active"].map((s) => (
        <button key={s} type="button" onClick={() => setState(state === s ? "all" : s)} className={`rounded-2xl p-4 text-left shadow-sm ${state === s ? "bg-navy text-white" : "bg-white text-navy"}`} data-testid={`onb-count-${s}`}>
          <p className="break-words text-[11px] font-medium uppercase leading-tight opacity-70">{t(`x.onb.state.${s}`)}</p><p className="text-2xl font-bold">{counts[s] || 0}</p>
        </button>))}</div>
      <BulkInvite onDone={r.refetch} />
      <div className="relative max-w-md"><Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("x.onb.search")} className={`${input} pl-9`} data-testid="onb-search" /></div>
      {r.isLoading ? <SkeletonBlock className="h-64" /> : !list.length ? <EmptyState icon={Users} title={t("x.cust.empty")} testId="onb-empty" /> : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="onb-list">{list.map((x) => <Row key={x.code} r={x} onInvite={setInvite} onResend={resend} />)}</ul>
      )}
      {invite && <InviteDialog row={invite} onClose={() => setInvite(null)} onDone={() => { setInvite(null); r.refetch(); }} />}
    </div>
  );
}
